import { useState, useEffect } from 'react';
import { 
    Carousel, 
    CarouselContent, 
    CarouselItem, 
    CarouselPrevious, 
    CarouselNext 
} from '../components/ui/carousel';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { X, Images, CaretLeft, CaretRight } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export function ImageGallery({ images = [], propertyTitle = '' }) {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [api, setApi] = useState(null);

    const openLightbox = (index) => {
        setCurrentIndex(index);
        setLightboxOpen(true);
    };

    useEffect(() => {
        if (api && lightboxOpen) {
            api.scrollTo(currentIndex);
        }
    }, [api, currentIndex, lightboxOpen]);

    if (!images || images.length === 0) {
        return (
            <div className="aspect-video bg-muted flex items-center justify-center rounded-sm">
                <div className="text-center text-muted-foreground">
                    <Images size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Keine Bilder vorhanden</p>
                </div>
            </div>
        );
    }

    const getImageUrl = (img) => {
        if (img.startsWith('http')) return img;
        return `${API}/api/public/files/${img}`;
    };

    return (
        <>
            {/* Main Gallery */}
            <div className="space-y-2">
                {/* Large Main Image */}
                <div 
                    className="aspect-video bg-muted rounded-sm overflow-hidden cursor-pointer relative group"
                    onClick={() => openLightbox(0)}
                    data-testid="gallery-main-image"
                >
                    <img
                        src={getImageUrl(images[0])}
                        alt={`${propertyTitle} - Bild 1`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1559329146-807aff9ff1fb?w=800';
                        }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 text-white px-4 py-2 rounded-sm">
                            Galerie öffnen
                        </div>
                    </div>
                    {images.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-sm">
                            1 / {images.length}
                        </div>
                    )}
                </div>

                {/* Thumbnail Grid */}
                {images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2" data-testid="gallery-thumbnails">
                        {images.slice(1, 5).map((img, idx) => (
                            <div 
                                key={idx}
                                className="aspect-video bg-muted rounded-sm overflow-hidden cursor-pointer relative group"
                                onClick={() => openLightbox(idx + 1)}
                            >
                                <img
                                    src={getImageUrl(img)}
                                    alt={`${propertyTitle} - Bild ${idx + 2}`}
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    onError={(e) => {
                                        e.target.src = 'https://images.unsplash.com/photo-1559329146-807aff9ff1fb?w=400';
                                    }}
                                />
                                {idx === 3 && images.length > 5 && (
                                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-semibold">
                                        +{images.length - 5}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox Dialog with Carousel */}
            <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
                <DialogContent className="max-w-5xl w-full p-0 bg-black/95 border-none">
                    <div className="relative">
                        {/* Close Button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-4 right-4 z-50 text-white hover:bg-white/20 rounded-full"
                            onClick={() => setLightboxOpen(false)}
                            data-testid="lightbox-close"
                        >
                            <X size={24} />
                        </Button>

                        {/* Image Counter */}
                        <div className="absolute top-4 left-4 z-50 text-white bg-black/50 px-3 py-1 rounded-sm text-sm">
                            {currentIndex + 1} / {images.length}
                        </div>

                        {/* Carousel */}
                        <Carousel 
                            setApi={setApi} 
                            className="w-full"
                            opts={{ startIndex: currentIndex }}
                        >
                            <CarouselContent>
                                {images.map((img, idx) => (
                                    <CarouselItem key={idx} className="flex items-center justify-center min-h-[70vh]">
                                        <img
                                            src={getImageUrl(img)}
                                            alt={`${propertyTitle} - Bild ${idx + 1}`}
                                            className="max-h-[80vh] max-w-full object-contain"
                                            onError={(e) => {
                                                e.target.src = 'https://images.unsplash.com/photo-1559329146-807aff9ff1fb?w=1200';
                                            }}
                                        />
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                            
                            {images.length > 1 && (
                                <>
                                    <CarouselPrevious className="left-4 bg-white/20 hover:bg-white/40 border-none text-white" />
                                    <CarouselNext className="right-4 bg-white/20 hover:bg-white/40 border-none text-white" />
                                </>
                            )}
                        </Carousel>

                        {/* Thumbnail Strip */}
                        {images.length > 1 && (
                            <div className="flex gap-2 p-4 overflow-x-auto justify-center bg-black/80">
                                {images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => {
                                            setCurrentIndex(idx);
                                            api?.scrollTo(idx);
                                        }}
                                        className={`flex-shrink-0 w-16 h-12 rounded-sm overflow-hidden border-2 transition-all ${
                                            currentIndex === idx ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
                                        }`}
                                    >
                                        <img
                                            src={getImageUrl(img)}
                                            alt={`Thumbnail ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.src = 'https://images.unsplash.com/photo-1559329146-807aff9ff1fb?w=100';
                                            }}
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
